/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2026 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <div class="h-100 d-flex flex-column">
    <div class="mb-3 p-2 bg-light rounded border">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center gap-2 flex-grow-1">
          <strong class="text-secondary text-nowrap">Rabaty pakietowe:</strong>
          <div v-if="currentPackageOrder" class="fw-bold text-primary">
            {{ currentPackageOrder.orderName }}
            <span v-if="currentPackageOrder.orderReference" class="text-muted fw-normal ms-2">
              ({{ currentPackageOrder.orderReference }})
            </span>
          </div>
        </div>

        <div>
          <button class="btn btn-sm btn-success shadow-sm text-nowrap" @click="openAddForm" :disabled="!currentOrderGroupGuid">
            <i class="fas fa-plus-circle me-1"></i> Dodaj rabat
          </button>
        </div>
      </div>
      <div
          v-if="wholesaler"
          class="mt-2 pt-2 border-top"
      >
        <div class="small text-muted mb-1">
          Rabaty dla wybranej hurtowni
        </div>
        <div class="fw-semibold text-dark">
          <i class="fas fa-truck me-2 text-secondary"></i>
          {{ wholesaler.name }}
        </div>
      </div>
    </div>

    <div class="flex-grow-1 position-relative">
      <CommonTable2
          v-if="currentOrderGroupGuid"
          :table="tableConfig"
          rowIdentifier="guid"
          :currentPage="currentPage"
          :totalRows="totalRows"
          :totalPages="totalPages"
          :showPaginationTop="false"
          :showPaginationBottom="true"
          :showMassActions="false"
          :multiselect="false"
          :enableRowSelection="true"
          @onItemAction="handleRowAction"
          @onPageChange="handlePageChange"
          :key="refreshKey"
      />

      <div v-else class="text-center p-5 text-muted fst-italic">
        Wybierz zamówienie, aby zarządzać rabatami.
      </div>

      <div v-if="currentOrderGroupGuid && tableConfig.data.length === 0"
           class="text-center p-4 text-muted border rounded bg-white mt-2">
        Brak zdefiniowanych rabatów pakietowych dla tego zamówienia.
      </div>
    </div>

    <PackageDiscountFormModal
        v-if="currentOrderGroupGuid"
        v-model:showModal="showFormModal"
        :initialData="selectedDiscount"
        :orderGroupGuid="currentOrderGroupGuid"
        :wholesalerSelectBoxVisible="wholesalerSelectBoxVisible"
        :preselectedWholesalerGuid="effectiveWholesalerGuid"
        @onPackageDiscountSave="saveDiscount"
    />
  </div>
</template>

<script>
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import PackageDiscountFormModal from "./PackageDiscountFormModal.vue";
import { usePackagesManagerModule } from "@/state/pinia/packagesManagerModule";
import Swal from "sweetalert2";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";

export default {
  name: "PackageDiscountsListPanel",
  components: { CommonTable2, PackageDiscountFormModal },
  props: {
    currentPackageOrder: { type: Object, required: true },
    wholesaler: { type: Object, default: null },
    includeAll: { type: Boolean, default: false },
    wholesalerSelectBoxVisible: { type: Boolean, default: false }
  },
  emits: ["onPackageDiscountSave", "onPackageDiscountDelete"],
  data() {
    return {
      showFormModal: false,
      selectedDiscount: null,
      refreshKey: 0,

      // Paginacja
      currentPage: 1,
      totalRows: 0,
      totalPages: 1,

      tableConfig: {
        headers: [
          { text: "Nazwa", key: "name", type: "text", width: "200px", sortable: true, style: "font-weight: 500;" },
          { text: "Typ", key: "typeLabel", type: "text", width: "100px" },
          { text: "Wartość init.", key: "formattedValue", type: "text", width: "120px", style: "font-weight:bold; color: #0078D4; text-align: right;" },
          { text: "Rozdzielny", key: "separateStr", type: "text", width: "100px", style: "font-size: 0.85rem;" },
          { text: "Utworzono", key: "createdAt", type: "datetime", width: "120px", isEpochMillis: true },
          { text: "Akcje", key: "actions", type: "actions", width: "100px" }
        ],
        data: []
      }
    };
  },
  computed: {
    packagesModule() {
      return usePackagesManagerModule();
    },
    currentOrderGroupGuid() {
      return this.currentPackageOrder ? this.currentPackageOrder.groupGuid : null;
    },
    lazyLoaderModule() { return useLazyLoaderModule(); },
    effectiveWholesalerGuid() {
      if (this.includeAll) return null;
      if(!this.wholesaler) return null;
      return this.wholesaler.guid || null;
    }
  },
  watch: {
    currentPackageOrder: {
      handler(val) {
        if (val) {
          this.currentPage = 1;
          this.refreshDiscounts();
        }
      },
      immediate: true
    }
  },
  methods: {

    saveDiscount(event){
      this.$emit("onPackageDiscountSave",event);
      this.refreshDiscounts();
    },

    refreshDiscounts() {
      if (!this.currentOrderGroupGuid) return;

      const params = {
        orderGroupGuid: this.currentOrderGroupGuid,
        page: this.currentPage,
        sortingKey: "createdAt",
        sortingDirection: "DESC"
      };

      if (this.includeAll) {
        params.includeAll = true;
      } else if (this.wholesaler) {
        params.wholesalerGuid = this.wholesaler.guid;
      }

      this.packagesModule.httpGetPackageDiscountsPaging(params).then((response) => {
        if (response && response.payload) {
          const pageData = response.payload;
          this.totalRows = pageData.totalElements;
          this.totalPages = pageData.totalPages;
          this.tableConfig.data = pageData.content.map(this.mapDiscountToRow);
          this.refreshKey++;

        } else {
          this.tableConfig.data = [];
          this.totalRows = 0;
        }

      })
    },

    mapDiscountToRow(discount) {
      return {
        ...discount,
        typeLabel: discount.type === 'PERCENT' ? 'Procentowy' : 'Kwotowy',
        formattedValue: discount.type === 'PERCENT' ? `${discount.initialValue}%` : `${discount.initialValue} PLN`,
        separateStr: discount.separateForEveryOrderItem ? 'Tak' : 'Nie',
        actions: [
          { icon: "fas fa-edit", action: "edit", tooltip: "Edytuj", enabled: true, class: "btn btn-sm btn-outline-primary" },
          { icon: "fas fa-trash-alt", action: "delete", tooltip: "Usuń", enabled: true, class: "btn btn-sm btn-outline-danger" }
        ]
      };
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.refreshDiscounts();
    },

    openAddForm() {
      this.selectedDiscount = null;
      this.showFormModal = true;
    },

    handleRowAction(payload) {
      const action = payload.action.action;
      const item = payload.itemData;

      if (action === "edit") {
        this.selectedDiscount = item;
        this.showFormModal = true;
      } else if (action === "delete") {
        this.confirmDelete(item);
      }
    },

    confirmDelete(discount) {
      Swal.fire({
        title: "Usunąć rabat?",
        text: `Czy na pewno chcesz usunąć rabat "${discount.name}"? Spowoduje to usunięcie przypisanych wartości.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Usuń",
        cancelButtonText: "Anuluj"
      }).then((result) => {
        if (result.isConfirmed) {
          this.lazyLoaderModule.setDisplayLazyLoader(true);
          this.packagesModule.httpDeletePackageDiscount({ guid: discount.guid }).then(() => {
            this.$emit("onPackageDiscountDelete", discount);
            this.refreshDiscounts();
          });
        }
      });
    }
  },
  mounted() {
    if (this.currentOrderGroupGuid) {
      this.refreshDiscounts();
    }
  }
};
</script>